import React, {useContext, useEffect, useState} from "react";
import {Box, Stack, Typography, IconButton, Divider, Button, Tooltip, CircularProgress} from "@mui/material";
import {
    IconLock, IconLockOpen, IconChevronLeft,
    IconPencil, IconTrash, IconRefresh
} from "@tabler/icons-react";
import {Context} from "../../../context/AppContext";
import {MCPToolsSection} from "./components/MCPToolsSection";
import {MCPPromptsSection} from "./components/MCPPromptsSection";
import EditMCPServerModal from "./components/EditMCPServerModal";
import useConfirm from "../../../hooks/useConfirm";
import {Link, useNavigate, useParams} from "react-router-dom";
import {toast} from "react-toastify";
import EditTokenModal from "./components/EditTokenModal";

export const MCPServerDetailPage = ({project}) => {
    const confirm = useConfirm();
    const navigate = useNavigate();
    const [server, setServer] = useState(null);

    const {state: {mcpServers}, updateMCPServer, reloadMCPServer, deleteMCPServer,editServerToken,getAllServersWithDetails} = useContext(Context);
    const [serverToEdit, setServerToEdit] = useState(null);
    const tools = server?.tools || [];
    const prompts = server?.prompts || {};
    const isSecure = server?.auth_token === true;
    const [tokenModalOpen, setTokenModalOpen] = useState(false);

    const {serverId} = useParams();


    useEffect(() => {
        if (project?.id) {
            if (!project?.configuration?.mcp_registry) {
                toast.info("Please update MCP client in configuration")
                navigate(`/project/${project?.id}`)
            }
        }
    }, [project.id]);

    useEffect(() => {
        if (serverId && mcpServers?.length) {
            const server = mcpServers?.find(i => +i.id === +serverId);
            setServer(server || null);
        }
    }, [serverId, mcpServers]);

    const handleToolSave = async (toolName, updated) => {
        const newTools = tools.map(t => t.name === toolName ? {...t, ...updated} : t);
        await updateMCPServer(server.id, {...server, tools: newTools});
    };

    const handlePromptSave = async (originalName, updated) => {
        const newPrompts = Object.fromEntries(
            Object.entries(prompts).map(([k, v]) =>
                k === originalName ? [updated.name ?? k, updated.text ?? v] : [k, v]
            )
        );
        await updateMCPServer(server.id, {...server, prompts: newPrompts});
    };

    const handleDelete = async (server) => {
        const ok = await confirm({
            title: "Delete MCP Server?",
            message: `This will permanently delete "${server.name}". This action cannot be undone.`,
            confirmText: "Delete",
        });
        if (!ok) return;
        try {
            await deleteMCPServer(server.id);
            await getAllServersWithDetails(project.id);
            navigate(`/project/${project.id}/mcp-registry`);
        } catch (e) {
            console.error("Failed to delete server:", e);
        }
    };

    if (server === null) {
        return <CircularProgress/>;
    }

    return (
        <Box sx={{
            maxWidth: "calc(100vw - 64px)",
            height: "100%",
            width: "90%",
            mx: "auto",
            minWidth: 0,
            boxSizing: "border-box",
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            px: 2,
            pt: 3,
            pb: "120px"
        }}>
            <EditMCPServerModal
                open={!!serverToEdit}
                onClose={() => setServerToEdit(null)}
                onSave={() => getAllServersWithDetails()}
                serverData={serverToEdit}
            />
            <Stack direction="row" gap={1}>
                <Box>
                    <IconButton size="small" component={Link} to={`/project/${project?.id}/mcp-registry`}>
                        <IconChevronLeft size={18}/>
                    </IconButton>
                </Box>
                <Box sx={{flex: 1}}>
                    <Typography variant="h4" fontWeight={600}>{server.name}</Typography>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        <Tooltip title={isSecure ? "Connection is secure" : "Connection may not be secure"}>
                            <Box component="span" sx={{
                                color: isSecure ? "info.main" : "warning.main",
                                display: "flex",
                                alignItems: "center"
                            }}>
                                {isSecure ? <IconLock size={13}/> : <IconLockOpen size={13}/>}
                            </Box>
                        </Tooltip>
                        <Typography fontSize={13} fontFamily="monospace" color="text.disabled">
                            {server.endpoint}
                        </Typography>
                    </Stack>
                </Box>

                <Box>
                    <Stack direction="row" gap={1}>
                        <Tooltip title="Reload">
                            <Button
                                variant="ghost" size="small"
                                onClick={() => reloadMCPServer(server)}
                                startIcon={<IconRefresh size={15}/>}
                                sx={{color: "grey.600", pl: 1, pr: 2}}
                            >
                                Reload
                            </Button>
                        </Tooltip>
                        <Tooltip title="Edit">
                            <Button
                                variant="ghost" size="small"
                                onClick={() => setServerToEdit(server)}
                                startIcon={<IconPencil size={15}/>}
                                sx={{color: "grey.600", pl: 1, pr: 2}}
                            >
                                Update
                            </Button>
                        </Tooltip>
                        <Tooltip title="Authorize">
                            <Button
                                variant="ghost" size="small"
                                onClick={() => setTokenModalOpen(true)}
                                startIcon={<IconLock size={15}/>}
                                sx={{color: "grey.600", pl: 1, pr: 2}}
                            >
                                Authorize
                            </Button>
                        </Tooltip>
                        <Divider orientation="vertical" variant="middle" flexItem/>
                        <Tooltip title="Delete">
                            <Button
                                variant="contained" color="secondary" size="small"
                                onClick={() => handleDelete(server)}
                                startIcon={<IconTrash size={15}/>}
                                sx={{borderRadius: 2}}
                            >
                                Delete
                            </Button>
                        </Tooltip>
                    </Stack>
                </Box>
            </Stack>
            <Stack direction={"column"} sx={{px: {xs: 0, md: 5}}}>

                <MCPToolsSection tools={tools} server={server} onToolSave={handleToolSave} onToolDeleted={() => getAllServersWithDetails(project.id)}/>

                <Divider sx={{width: "90%", mx: "auto"}}/>

                <MCPPromptsSection prompts={prompts} onPromptSave={handlePromptSave}/>
            </Stack>
            <EditTokenModal
                open={tokenModalOpen}
                onClose={() => setTokenModalOpen(false)}
                server={server}
                onSave={async (newToken) => {
                    await editServerToken(server.id, newToken);
                    setTokenModalOpen(false);
                }}
            />
        </Box>
    );
};

export default MCPServerDetailPage;
